// FILE: packages/components/src/admin/sprints/SprintBacklogPanel.tsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { Spinner, IconButton } from '@dopomogai/ui';
import { DraggableTaskCard } from '../tasks/DraggableTaskCard';
import type { ProjectTask } from '@dopomogai/types';
import { useUiStore } from '../../../../../../apps/website/src/stores/ui'; // Adjust path

interface SprintBacklogPanelProps {
  tasks: ProjectTask[];
  isLoading?: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

/**
 * A panel that displays the list of tasks assigned to a sprint but not yet
 * planned into a specific day. It can now be collapsed.
 */
export const SprintBacklogPanel: React.FC<SprintBacklogPanelProps> = ({
  tasks,
  isLoading,
  isCollapsed,
  onToggleCollapse,
}) => {
  const { t } = useTranslation('admin_sprints');
  // --- FIX: Use the new function from the refactored store ---
  const { openTaskInspector } = useUiStore();

  if (isCollapsed) {
    return (
      <div className="h-full bg-slate-100 border-r-2 border-black flex flex-col items-center p-2">
          <IconButton
            icon={<PanelLeftOpen size={20} />}
            onClick={onToggleCollapse}
            tooltip={t('backlog.expandTooltip')}
            ariaLabel={t('backlog.expandTooltip')}
          />
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-100 border-r-2 border-black flex flex-col min-w-[320px]">
      <header className="p-4 border-b-2 border-black flex justify-between items-center flex-shrink-0 bg-white">
        <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-slate-800">{t('backlog.sprintBacklogTitle')}</h3>
            <span className="font-bold text-slate-500 bg-slate-200 px-2.5 py-1 rounded-full text-sm">
                {tasks.length}
            </span>
        </div>
        <IconButton
            icon={<PanelLeftClose size={20} />}
            onClick={onToggleCollapse}
            tooltip={t('backlog.collapseTooltip')}
            ariaLabel={t('backlog.collapseTooltip')}
        />
      </header>
      <div className="p-4 overflow-y-auto flex-grow">
        {isLoading && <div className="p-4 flex justify-center"><Spinner /></div>}
        {!isLoading && tasks.map(task => (
            <div className="mb-3" key={task._id}>
                <DraggableTaskCard 
                    task={task} 
                    onExpand={() => openTaskInspector(task, { projectId: task.project_id, sprintId: task.sprint_id ?? undefined })}
                />
            </div>
        ))}
      </div>
    </div>
  );
};